import cn from 'classnames';
import {
  ChangeEvent,
  ClipboardEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';

import styles from './CodeInput.module.scss';

const NON_DIGITS = /\D/g;

type CodeInputProps = {
  value: string;
  length?: number;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  testID?: string;
  describedBy?: string;
  onChange: (value: string) => void;
  digitLabel?: (position: number, total: number) => string;
  onComplete?: (value: string) => void;
};

/**
 * `value` holds the digits entered so far, left-packed: a box is filled only
 * when every box before it is, which is what lets a plain string stand in for
 * the boxes everywhere else.
 */
export const CodeInput: FC<CodeInputProps> = (props) => {
  const {
    value,
    length = 6,
    error,
    disabled,
    autoFocus,
    className,
    testID,
    describedBy,
    onChange,
    digitLabel,
    onComplete,
  } = props;

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const boxIndexes = [...Array(length).keys()];

  const focusBox = (index: number) => {
    inputsRef.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  // A rejected code leaves the boxes disabled, and disabling a focused input
  // blurs it, so without this a keyboard-only user has no way back to them.
  useEffect(() => {
    if (error && !disabled) {
      focusBox(value.length);
    }
  }, [error, disabled]);

  const emit = (nextValue: string) => {
    onChange(nextValue);

    if (nextValue.length === length) {
      onComplete?.(nextValue);
    }
  };

  const handleChange =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const typed = event.target.value.replace(NON_DIGITS, '');

      if (!typed) {
        return;
      }

      // A browser autofilling the one-time code delivers every digit to the
      // box that had focus, so a multi-character value fills forward from it.
      const digits = typed.slice(0, length - index);
      const nextValue = (
        value.slice(0, index) +
        digits +
        value.slice(index + digits.length)
      ).slice(0, length);

      emit(nextValue);
      focusBox(index + digits.length);
    };

  const handleKeyDown =
    (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (value.length === length) {
          onComplete?.(value);
        }
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();

        const target = value[index] ? index : index - 1;

        emit(value.slice(0, target) + value.slice(target + 1));
        focusBox(target);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        focusBox(index - 1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        focusBox(index + 1);
      }
    };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pasted = event.clipboardData
      .getData('text')
      .replace(NON_DIGITS, '')
      .slice(0, length);

    if (!pasted) {
      return;
    }

    emit(pasted);
    focusBox(pasted.length);
  };

  return (
    <div
      className={cn(styles.container, disabled && styles.disabled, className)}
      data-component="code-input"
      data-testid={testID}
    >
      {boxIndexes.map((index) => (
        <input
          key={index}
          ref={(element) => {
            inputsRef.current[index] = element;
          }}
          className={cn(styles.box, error && styles.error)}
          value={value[index] ?? ''}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={digitLabel?.(index + 1, length)}
          aria-invalid={error}
          aria-describedby={describedBy}
        />
      ))}
    </div>
  );
};
